import { Snackbar, Alert } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, Outlet } from 'react-router-dom';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { makeStyles } from '@mui/styles';
import { Typography, Menu, IconButton, MenuItem, Popover, Box, Dialog, DialogTitle, DialogContent, Select, DialogActions, Button, useMediaQuery, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { signOut as signOutAction } from '../redux/actions/authActions';
import { getUser as getUserAction } from '../redux/actions/userActions';
import MessageIcon from '@mui/icons-material/Message';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationPanel from './NotificationPanel';
import { getLocationAction, switchUserLocationAction } from '../redux/actions/createCustomerActions';
import { getUserInfoAction } from '../redux/actions/agencyActions';
import Tooltip from '@mui/material/Tooltip';
import ResponsiveMenu from './ResponsiveMenu';
import { getThemeAction } from '../redux/actions/themeActions';
import { getThemeSelectedLogoAction } from '../redux/actions/themeAction';
import { useThemeLogoUrl } from "../common/useThemeLogoUrl";

const useStyles = makeStyles((customTheme) => {
  return {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexDirection: 'row',
      fontFamily: "'Poppins', sans-serif",
      fontSize: "1rem",
      padding: '0.5em 1em',
      width: '100%',
      boxSizing: 'border-box',
    },
    nav: {
      padding: '0.5em',
      margin: '0',
      fontFamily: "'Poppins', sans-serif",
      fontSize: "1rem",
    },
    logo: {
      maxWidth: '120px',
      width: '100%',
      height: 'auto',
      fontWeight: 'bold',
      fontFamily: "'Poppins', sans-serif",
    },
    link: {
      textDecoration: "none",
      color: customTheme?.palette?.primary?.main,
      fontFamily: "'Poppins', sans-serif",
      fontSize: "1rem",
      "&:hover": {
        color: 'black',
      }
    },
    icon: {
      color: customTheme?.palette?.primary?.main,
      fontFamily: "'Poppins', sans-serif",
    },
    menuLink: {
      color: '#000000',
      textDecoration: "none",
      padding: "0.25em",
      fontFamily: "'Poppins', sans-serif",
      fontSize: "0.9rem",
      display: 'block',
      width: '100%',
    },
    headerContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.5em 1em',
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      width: '100%',
      boxSizing: 'border-box',
    },
    iconsContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      flexWrap: 'nowrap',
      marginLeft: '10px'
    },
    userInfo: {
      fontSize: '0.875rem',
      margin: 0,
      marginLeft: '8px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '120px',
      '@media (max-width: 768px)': {
        display: 'none',
      },
    },
    badge: {
      position: 'absolute',
      backgroundColor: '#FF0000',
      color: '#FFF',
      borderRadius: '50%',
      fontSize: '10px',
      width: '16px',
      height: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      top: '2px',
      right: '2px',
      '@media (max-width: 480px)': {
        width: '14px',
        height: '14px',
        fontSize: '9px',
      },
    },
    dialogContent: {
      padding: '16px',
      minWidth: '280px',
      width: '100%',
      boxSizing: 'border-box',
    },
    dialogActions: {
      padding: '8px 16px',
      gap: '8px',
    },
  };
});

const RaterHeader = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationChanged, setLocationChanged] = useState(false);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const user = useSelector((state) => state?.user?.user);
  const total_unread_count = useSelector((state) => state.communication.total_message_count || 0);
  const notificationCount = useSelector(state => state.notificationPanel.countNotifications);
  const user_info_data = useSelector((state) => state.agency.user_info || [])
  const user_location = useSelector((state) => state.createCustomer.get_location_details || [])
  const themeLogoUrl = useThemeLogoUrl();

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    } else {
      dispatch(getUserAction());
    }
  }, [user, dispatch]);

  useEffect(() => {
      dispatch(getThemeAction())
  }, [dispatch]);

  useEffect(() => {
    dispatch(getUserInfoAction());
  }, [dispatch])

  useEffect(() => {
    dispatch(getLocationAction());
  },[dispatch])

  const roleAssigned = user?.newRole || [];
  const hasAuditorRole = roleAssigned.includes("ROLE_AUDITOR");

  const handleMenu = (event, target) => {
    setOpen(target);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(undefined);
    setAnchorEl(null);
  };

  const handleChatIconClick = () => {
    navigate("/chat");
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNavigation = (path) => () => {
    setOpen(undefined);
    setAnchorEl(null);
    navigate(path);
  };

  const logout = async() => {
    setOpen(undefined);
    setAnchorEl(null);
    await dispatch(signOutAction());
  };

  const handleLocationUpdate = async () => {
    if (selectedLocation) {
      setIsUpdatingLocation(true);
      try {        
        await dispatch(switchUserLocationAction(selectedLocation));
        await dispatch(getUserInfoAction());
        
        setLocationChanged(true);
        setLocationDialogOpen(false);
        setSelectedLocation('');

        setTimeout(() => {
          window.location.reload();
        }, 1000); 
        
      } catch (error) {
        console.error('Error updating location:', error);
        setLocationDialogOpen(false);
        setSelectedLocation('');
      } finally {
        setIsUpdatingLocation(false);
      }
    } else {
      setLocationDialogOpen(false);
      setSelectedLocation('');
    }
  };

  return (
    <>
      <Box className='header-container'  sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <Typography variant="h4" sx={{ flexShrink: 0 }}>
          <Link to="/home">
            {themeLogoUrl && (  // no flash: we only show once we know it
              <img
                src={themeLogoUrl}
                alt="Logo"
                className={classes.logo}
              />
            )}
          </Link>
        </Typography>


        
        <Box className={classes.iconsContainer}>
          <ResponsiveMenu />

          {!hasAuditorRole && (
            <IconButton 
              onClick={handleChatIconClick} 
              aria-label="Chat" 
              sx={{ 
                color: "#000000", 
                position: 'relative',
                padding: isMobile ? '0px' : '8px'
              }}
              size={isMobile ? "small" : "medium"}
            >
              <MessageIcon sx={{ 
                width: isMobile ? 20 : 25, 
                height: isMobile ? 20 : 22.8 
              }} />
              {total_unread_count > 0 && (
                <Box sx={{
                  position: 'absolute',
                  backgroundColor: '#FF0000',
                  color: '#FFF',
                  padding: '4px',
                  borderRadius: '100%',
                  fontSize: '12px',
                  width: '20px',
                  height: '20px',
                  top: '-4px',
                  right: '-5px',
                }}>
                  {total_unread_count}
                </Box>
              )}
            </IconButton>
          )}

          {!hasAuditorRole && (
            <IconButton
              onClick={handleNotificationClick}
              aria-label="Notifications"
              sx={{ 
                color: "#000000", 
                position: 'relative',
                padding: isMobile ? '0px' : '8px'
              }}
              size={isMobile ? "small" : "medium"}
            >
              <NotificationsIcon sx={{ 
                width: isMobile ? 20 : 25, 
                height: isMobile ? 20 : 22.8 
              }} />
              {notificationCount > 0 && (
                <Box sx={{
                  position: 'absolute',
                  backgroundColor: '#FF0000',
                  color: '#FFF',
                  padding: '4px',
                  borderRadius: '100%',
                  fontSize: '12px',
                  width: '20px',
                  height: '20px',
                  top: '-4px',
                  right: '-5px',
                }}>
                  {notificationCount}
                </Box>
              )}
            </IconButton>
          )}
          
          <IconButton
            sx={{ 
              color: "#000000", 
              position: 'relative',
              padding: isMobile ? '0px' : '8px'
            }}
            size={isMobile ? "small" : "medium"}
            onClick={() => {
              const currentLocation = user_location?.find(loc => loc.locationDescription === user_info_data.defaultLocation);
              if (currentLocation) {
                setSelectedLocation(currentLocation.locationId);
              }
              setLocationDialogOpen(true);
            }}
            
          >
            <Tooltip 
              title={user_info_data.defaultLocation} 
              placement="bottom" 
              disableScrollLock={true}
            >
              <EditLocationIcon sx={{ 
                width: isMobile ? 20 : 24, 
                height: isMobile ? 20 : 24 
              }} />
            </Tooltip>
          </IconButton>

          <IconButton
            onClick={(e) => handleMenu(e, 'user-menu')}
            sx={{
              color: "#000000",
              position: 'relative',
              padding: isMobile ? '0px' : '8px',
              '&:hover': {
                backgroundColor: 'transparent', 
              }
            }}
            size={isMobile ? "small" : "medium"}
          >
            <AccountCircleIcon
              sx={{
                position: 'relative',
                overflow: 'hidden',
                flexShrink: 0,
                zIndex: 1,
                color: '#000000',
                width: isMobile ? 20 : 24,
                height: isMobile ? 20 : 24,
              }}
              loading="lazy"
              alt=""
            />
            <Typography
                variant="body2"
                className={classes.userInfo}
                sx={{ color: 'var(--primary-color-contrast)', fontSize: isMobile ? '0.75rem' : '1rem' }}
              >
                {user_info_data.fullName}
            </Typography>
          </IconButton>

          <Menu
            id="usermenu"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            open={open === 'user-menu'}
            onClose={handleClose}
            anchorEl={anchorEl}
            PaperProps={{
              style: {
                width: isMobile ? '150px' : '150px',
                maxWidth: '100%',
                marginTop: '40px',
                marginLeft:'15px'
              }
            }}
          >
            {!hasAuditorRole && [
              <MenuItem key="edit-profile" onClick={handleNavigation('/edituserprofile')}>
                <Link className={classes.menuLink}>Edit Profile</Link>
              </MenuItem>,
              <MenuItem key="settings" onClick={handleNavigation('/settings')}>
                <Link className={classes.menuLink}>Settings</Link>
              </MenuItem>,
              <MenuItem key="custom-theme" onClick={handleNavigation('/customthemesettings')}>
                <Link className={classes.menuLink}>Custom Theme</Link>
              </MenuItem>
            ]}

            <MenuItem onClick={logout}>
              <Link className={classes.menuLink}>Logout</Link>
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Dialog 
        open={locationDialogOpen} 
        onClose={() => setLocationDialogOpen(false)} 
        disableScrollLock={true}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            m: isMobile ? 1 : 1,
            width: '100%',
            maxWidth: isMobile ? '300px' : '350px',
            marginRight: isMobile ? '75px' : '0px'
          }
        }}
      >
        <DialogTitle
          className="btn-primary"
          sx={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 16px",
            fontSize: isMobile ? '1rem' : '1.25rem',
          }}
         
        >
          Select Location
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box sx={{ mt: isMobile ? 1 : 2 }}>
            <Select
              fullWidth
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              displayEmpty
              size={isMobile ? "small" : "medium"}
            >
              <MenuItem value="" disabled>Select a location</MenuItem>
              {user_location?.map((loc) => (
                <MenuItem key={loc.locationId} value={loc.locationId}>
                  {loc.locationDescription}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
          variant='outlined' 
            onClick={() => {
              setLocationDialogOpen(false);
              setSelectedLocation('');
            }}
            disabled={isUpdatingLocation}
          >
            Cancel
          </Button>
          <Button 
            variant='outlined' 
            onClick={handleLocationUpdate}
            disabled={isUpdatingLocation || !selectedLocation}
          >
            {isUpdatingLocation ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={locationChanged}
        autoHideDuration={3000}
        onClose={() => setLocationChanged(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbar-root': {
            position: 'fixed',
            zIndex: 1400,
          }
        }}
      >
        <Box>
          <Alert severity="success" onClose={() => setLocationChanged(false)}>
            Location updated successfully!
          </Alert>
        </Box>
      </Snackbar>

      {!hasAuditorRole && (
        <Popover
          open={Boolean(notificationAnchorEl)}
          anchorEl={notificationAnchorEl}
          onClose={handleNotificationClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          
          >
          <NotificationPanel onClose={handleNotificationClose} />
        </Popover>
      )}
      <main>
        <Outlet />
      </main>
    </>
  );
};

export default RaterHeader;